/*
 * CompuMac: gallery per colour (CompuHub, 2026-09-29).
 *
 * Which pictures belong to which colour is decided on the server (snippet cm-media-groups, product metafield
 * compuhub.media_groups). When the customer picks another colour, Dawn (product-info.js) re-renders the
 * section and syncs the big pictures and the zoom from it — but not the thumbnails. This script copies the
 * thumbnail list from the same re-rendered section, so the thumbnails always show the chosen colour's
 * pictures, and re-wires them to the gallery.
 *
 * Nothing happens on products without colour groups (no data-cm-grouped on either side): Dawn's own
 * behaviour stays untouched there. The rule lives in Liquid only; copying the server's answer keeps this
 * script free of a second copy of it.
 */
(() => {
  if (window.cmMediaGroups) return;
  window.cmMediaGroups = true;
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  // Texte in der Sprache der Seite: window.cmT.media_groups aus snippets/cm-x-js-texts (Texte in snippets/cm-xt-js, Abschnitt media_groups).
  // Fehlt cmT oder ein Schlüssel, gilt der deutsche Text hier; {name} wird ersetzt.
  const cmTexts = (window.cmT && window.cmT.media_groups) || {};
  const tx = (key, de, vars) => {
    const own = cmTexts[key];
    const text = typeof own === 'string' && own && !/^(translation missing|\[text fehlt)/i.test(own) ? own : de;
    return vars ? text.replace(/\{(\w+)\}/g, (all, name) => (name in vars ? vars[name] : all)) : text;
  };

  const galleryFor = (sectionId) => {
    for (const info of document.querySelectorAll('product-info')) {
      const id = info.dataset.originalSection || info.dataset.section;
      if (id === sectionId) return info.querySelector('media-gallery');
    }
    return null;
  };

  const announce = (gallery, count) => {
    const region = gallery.querySelector('[id^="GalleryStatus"]');
    if (!region) return;
    region.setAttribute('aria-hidden', 'false');
    region.textContent = count === 1 ? tx('count_one', '1 Bild für diese Auswahl') : tx('count_other', '{count} Bilder für diese Auswahl', { count });
    setTimeout(() => region.setAttribute('aria-hidden', 'true'), 2000);
  };

  const update = (gallery, fresh) => {
    const liveId = gallery.id.replace(/^MediaGallery-/, '');
    const freshId = fresh.id.replace(/^MediaGallery-/, '');
    // Quick add renames the section's ids; the fresh HTML carries the original ones.
    const rename = (html) => (liveId === freshId ? html : html.split(freshId).join(liveId));

    gallery.toggleAttribute('data-cm-grouped', fresh.hasAttribute('data-cm-grouped'));

    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    const freshThumbs = fresh.querySelector('[id^="GalleryThumbnails"]');
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const freshViewer = fresh.querySelector('[id^="GalleryViewer"]');

    // Counter of the big pictures on phones.
    const total = viewer?.querySelector('.slider-counter--total');
    const freshTotal = freshViewer?.querySelector('.slider-counter--total');
    if (total && freshTotal) total.textContent = freshTotal.textContent;
    const buttons = viewer?.querySelector('.slider-buttons');
    const freshButtons = freshViewer?.querySelector('.slider-buttons');
    if (buttons && freshButtons) buttons.className = freshButtons.className;
    viewer?.querySelectorAll('li[data-media-id]').forEach((li) => {
      const twin = freshViewer?.querySelector(`li[data-media-id="${CSS.escape(li.dataset.mediaId.split(liveId).join(freshId))}"]`);
      if (twin) li.classList.toggle('product__media-item--single', twin.classList.contains('product__media-item--single'));
    });

    if (!thumbs) return;

    if (!freshThumbs) {
      // One picture for this colour: no thumbnails, as Dawn renders it.
      thumbs.hidden = true;
      return;
    }

    const focused = thumbs.contains(document.activeElement);
    const list = thumbs.querySelector('ul');
    const freshList = freshThumbs.querySelector('ul');
    if (!list || !freshList) return;

    thumbs.hidden = false;
    thumbs.className = freshThumbs.className;
    thumbs.querySelectorAll(':scope > button.slider-button').forEach((button) => {
      const twin = freshThumbs.querySelector(`:scope > button.slider-button[name="${button.name}"]`);
      if (twin) button.className = twin.className;
    });
    list.innerHTML = rename(freshList.innerHTML);

    list.querySelectorAll('[data-target] button').forEach((button) => {
      const target = button.closest('[data-target]').dataset.target;
      button.addEventListener('click', () => gallery.setActiveMedia(target, false));
    });

    if (typeof thumbs.resetPages === 'function') thumbs.resetPages();
    if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();

    const active = viewer?.querySelector('li[data-media-id].is-active') || viewer?.querySelector('li[data-media-id]');
    const activeThumb = active && list.querySelector(`[data-target="${CSS.escape(active.dataset.mediaId)}"]`);
    if (activeThumb && typeof gallery.setActiveThumbnail === 'function') gallery.setActiveThumbnail(activeThumb);
    if (focused) (activeThumb?.querySelector('button') || list.querySelector('li:not([hidden]) button'))?.focus({ preventScroll: true });

    announce(gallery, list.querySelectorAll('li:not([hidden])').length);
  };

  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    try {
      const data = event && event.data;
      if (!data || !data.html) return;
      const gallery = galleryFor(data.sectionId);
      const fresh = data.html.querySelector('media-gallery');
      if (!gallery || !fresh) return;
      if (!gallery.hasAttribute('data-cm-grouped') && !fresh.hasAttribute('data-cm-grouped')) return;
      update(gallery, fresh);
    } catch (error) {
      // A gallery that could not be updated keeps Dawn's; buying must never break over a picture.
      console.warn('[cm-media-groups]', error);
    }
  });
})();
